import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { EntityHeader } from '../entity-header/EntityHeader';
import { LegacyEntityHeader } from '../entity-header/LegacyEntityHeader';
import { ChartPrice } from '../simple-chart/components/chart-price/ChartPrice';
import { getTabs } from '../tab-content/utils';
import styles from './AssetOverviewHeader.module.scss';

export interface AssetOverviewHeaderProps {
  snakeMeta?: SnakeMeta;
  entity: Entity;
  activeTab: TabsType;
  changeTab: (index: number, tabKey: TabsType) => void;
  showShadow: boolean;
  documentsCount?: number;
}

export const AssetOverviewHeader = ({
  snakeMeta,
  entity,
  activeTab,
  changeTab,
  showShadow,
  documentsCount = 0,
}: AssetOverviewHeaderProps) => {
  const { isNewEarningsTabEnabled, isAoPulseEnabled } = useFlags(state => ({
    isNewEarningsTabEnabled: state.getFeatureFlagState(
      'PLATFORM-4671-ao-earnings'
    ),
    isAoPulseEnabled: state.getFeatureFlagState('PLATFORM-5199-ao-pulse'),
  }));

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isEcoCentralBank =
    entity.sub_class === AssetSubClassCode.EcoCentralBank;
  const isDocumentsTabVisible =
    (isStock || isEcoCentralBank) && !!documentsCount;
  const tabs = getTabs({
    options: { isStock, isDocumentsTabVisible },
    isNewEarningsTabEnabled,
  });
  const filteredTabs = tabs.filter(tab => !tab.isHidden);

  return (
    <div
      className={clsx(styles.header, {
        [styles.showShadow]: showShadow,
      })}
    >
      <div className={styles.layout}>
        <div
          className={clsx(styles.headerContent, {
            [styles.noEntity]: !entity,
          })}
        >
          {entity && (
            <>
              <div className={styles.contentWrapper}>
                {isAoPulseEnabled ? (
                  <EntityHeader
                    entity={entity}
                    priceDisplayNode={
                      <ChartPrice
                        entity={entity}
                        snakeMeta={snakeMeta}
                        enablePriceHover={false}
                      />
                    }
                  />
                ) : (
                  <LegacyEntityHeader
                    entity={entity}
                    priceDisplayNode={
                      <ChartPrice
                        entity={entity}
                        snakeMeta={snakeMeta}
                        enablePriceHover={false}
                      />
                    }
                  />
                )}
              </div>
              <TabLines
                tabs={filteredTabs}
                activeTab={activeTab}
                onChange={changeTab}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
